import { TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import type { ChangeRequestFieldChange, ChangeRequestFieldType } from "../api/workflowTypes";
import { stringComparerBy } from "../utilities/arrayHelper";
import { convertISODate } from "../utilities/dateUtils";
import { formatNumber } from "../utilities/formatters";
import RoundedTable from "./RoundedTable";
import TypographyTooltipEllipsis from "./TypographyTooltipEllipsis";

interface DiffValueTextProps {
  value: string | number | undefined;
  fieldType: ChangeRequestFieldType;
  wrapValue?: boolean;
}

const DiffValueText = ({ value, fieldType, wrapValue }: DiffValueTextProps) => {
  if (value === undefined || value === null) {
    return null;
  }

  switch (fieldType) {
    case "Text": {
      return value ? (
        wrapValue ? (
          <Typography>{value.toString()}</Typography>
        ) : (
          <TypographyTooltipEllipsis text={value.toString()} />
        )
      ) : null;
    }
    case "Decimal": {
      return typeof value === "number" ? <Typography>{formatNumber(value)}</Typography> : null;
    }
    case "DateOnly": {
      return value ? <Typography>{convertISODate(value.toString())}</Typography> : null;
    }
    default: {
      return null;
    }
  }
};

interface BodyRowProps {
  diff: ChangeRequestFieldChange;
  showOldValue: boolean;
  wrapValues?: boolean;
}

const BodyRow = ({ diff, showOldValue, wrapValues }: BodyRowProps) => (
  <TableRow>
    <TableCell sx={(t) => ({ width: 200, color: t.palette.text.secondary })}>{diff.displayName || diff.key}</TableCell>
    {showOldValue && (
      <TableCell sx={{ maxWidth: 250 }}>
        <DiffValueText value={diff.oldValue} fieldType={diff.fieldType} wrapValue={wrapValues} />
      </TableCell>
    )}
    <TableCell sx={{ maxWidth: 250 }}>
      <DiffValueText value={diff.newValue} fieldType={diff.fieldType} wrapValue={wrapValues} />
    </TableCell>
  </TableRow>
);

interface HeadRowProps {
  oldValueColumnLabel?: string;
  newValueColumnLabel: string;
}

const HeadRow = ({ oldValueColumnLabel, newValueColumnLabel }: HeadRowProps) => (
  <TableRow>
    <TableCell>Field</TableCell>
    {oldValueColumnLabel && <TableCell>{oldValueColumnLabel}</TableCell>}
    <TableCell sx={(t) => ({ color: t.palette.success.light })}>{newValueColumnLabel}</TableCell>
  </TableRow>
);

interface Props {
  fieldChanges: ChangeRequestFieldChange[];
  oldValueColumnLabel?: string;
  newValueColumnLabel: string;
  sortKeys?: boolean;
  wrapValues?: boolean;
}

const FieldsDiffTable = ({ fieldChanges, oldValueColumnLabel, newValueColumnLabel, sortKeys, wrapValues }: Props) => {
  const displayedFieldChanges = sortKeys ? [...fieldChanges].sort(stringComparerBy((d) => d.key)) : fieldChanges;

  return (
    <RoundedTable>
      <TableHead>
        <HeadRow oldValueColumnLabel={oldValueColumnLabel} newValueColumnLabel={newValueColumnLabel} />
      </TableHead>
      <TableBody>
        {displayedFieldChanges.map((diff) => (
          <BodyRow key={diff.key} diff={diff} showOldValue={!!oldValueColumnLabel} wrapValues={wrapValues} />
        ))}
      </TableBody>
    </RoundedTable>
  );
};

export default FieldsDiffTable;
